import React, { useEffect, useState, useCallback } from "react";
import { Button, message, Avatar, Col, Spin } from "antd";
import { EditOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import useAuth from "../../../hooks/useAuth";
import instance from "../../../api/cmsapi";
import QuillHtmlDisplay from "components/QuillHtmlDisplay";
import { quillFormats, quillModules } from "utils/quillEditorConfig";

function WorkFlowComponent({ year, district, assessmentStatus, tableCommented, type, userRole, item }) {
  const { user } = useAuth();
  const [editorContent, setEditorContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [existingCommentId, setExistingCommentId] = useState(null);
  const [singleComment, setSingleComment] = useState(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);

  const currentUsername = user?.user?.username || "";
  const currentFullName = user?.user?.fullName || "";
  const districtId = district;

  // Check if user has permission to edit based on userRole
  const hasEditPermission = () => {
    const allowedRoles = ['Superuser', 'RCC USER'];
    return allowedRoles.includes(userRole?.name);
  };

  // Check if user can edit (has permission AND either no comment exists OR is the creator)
  const canEdit = () => {
    // First check if assessment status is 'Pending'
    if (assessmentStatus?.status !== 'Pending') {
      return false;
    }
    
    // Check if user has proper role
    if (!hasEditPermission()) {
      return false;
    }
    
    // If no comment exists, user can create one
    if (!singleComment) {
      return true;
    }
    
    // If comment exists, only the creator can edit
    return singleComment.username === currentUsername;
  };

  // Fetch single comment from /comments/table endpoint
  const fetchSingleComment = useCallback(async () => {
    if (!districtId || !year || !tableCommented) {
      return;
    }

    setLoading(true);
    try {
      const response = await instance.get(`/comments/table/${districtId}/${year}/${type}/${tableCommented}`);      
      // Handle both array and object responses
      let commentData = null;
      if (Array.isArray(response?.data) && response.data.length > 0) {
        // If it's an array, get the first item (assuming only one comment per table)
        commentData = response.data[0];
      } else if (response?.data && !Array.isArray(response?.data)) {
        // If it's a single object
        commentData = response.data;
      }
      
      if (commentData && commentData.id) {
        setSingleComment(commentData);
        setExistingCommentId(commentData.id);
        setEditorContent(commentData.comments || "");
      } else {
        setSingleComment(null);
        setExistingCommentId(null);
        setEditorContent("");
      }
      
      setError(null);
    } catch (error) {
      console.error("Error fetching comment:", error);
      if (error.response?.status === 404) {
        // No comment exists yet
        setSingleComment(null);
        setExistingCommentId(null);
        setEditorContent("");
      } else {
        setError(`Failed to load ${item}`);
      }
    } finally {
      setLoading(false);
    }
  }, [districtId, year, tableCommented, type]);

  // Fetch comment on mount or when dependencies change
  useEffect(() => {
    fetchSingleComment();
  }, [fetchSingleComment]);

  const handleSave = async () => {
    if (!editorContent.trim()) {
      message.error("Comment cannot be empty");
      return;
    }
    if (!currentUsername || !districtId || !year) {
      message.error("User or Region information is missing");
      return;
    }
    
    // Check edit permissions before saving
    if (!canEdit()) {
      message.error(`You don't have permission to save the ${item}`);
      return;
    }

    const commentDate = new Date().toISOString().split("T")[0].split("-").map(Number);
    const payload = {
      id: existingCommentId || 0,
      username: currentUsername,
      fullName: currentFullName,
      userRole: userRole?.name || "",
      type: type,
      districtId: districtId,
      year: year,
      tableCommented: tableCommented,
      comments: editorContent,
      commentDate: commentDate,
      updateDate: commentDate,
      dddpDataDate: null,
      dddpData: {},
    };

    try {
      setLoading(true);
      if (existingCommentId) {
        // Update existing comment
        await instance.put(`/comments/${existingCommentId}`, payload);
        setSingleComment({ ...singleComment, comments: editorContent, updateDate: commentDate });
        message.success(`${item} updated successfully`);
        setEditing(false);
      } else {
        // Create new comment
        const response = await instance.post("/comments", payload);
        setSingleComment(response.data);
        setExistingCommentId(response.data.id);
        message.success(`${item} added successfully`);
        setEditing(false);
      }
    } catch (error) {
      message.error(`Failed to save ${item}`);
      console.error("Save error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    // Reset to original content
    if (singleComment) {
      setEditorContent(singleComment.comments);
    } else {
      setEditorContent("");
    }
  };

  // Show loading state
  if (loading && !singleComment) {
    return (
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h3>{item.toUpperCase()}</h3>
          </div>
          <div className="card-body">
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin tip={`Loading ${item}...`} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-12">
      <div className="card">
        <div className="card-header">
          <h3>{item.toUpperCase()}</h3>
         
        </div>
        <div className="card-body">
          <div style={{ padding: "0px" }}>
            {/* Editor - only show if user can edit */}
            {canEdit() && (
              <div style={{ display: editing || !singleComment ? "block" : "none" }}>
                <ReactQuill
                  value={editorContent}
                  onChange={setEditorContent}
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ height: "500px", marginBottom: "50px" }}
                  readOnly={loading}
                />
                <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                  <Button
                    type="primary"
                    onClick={handleSave}
                    loading={loading}
                    disabled={loading}
                  >
                    <span style={{ color: "white", fontSize: "14px", fontWeight: "bold" }}>
                      Save {item}
                    </span>
                  </Button>
                  {editing && (
                    <Button onClick={handleCancelEdit} disabled={loading}>
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Display the comment - Only show if there's a comment and not editing */}
            {!editing && singleComment && (
              <div
                style={{
                  borderTop: "1px solid #e8e8e8",
                  padding: "8px",
                  background: "#fff",
                  width: "100%",
                  marginTop: "20px",
                }}
              >
                <div
                  style={{
                    padding: "10px",
                    border: "1px solid #f0f0f0",
                    borderRadius: "6px",
                    marginBottom: "10px",
                  }}
                >
                  <div style={{ display: "flex", marginBottom: "10px" }}>
                    <Col>
                      <Avatar
                        src={singleComment.userImage || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTL_JlCFnIGX5omgjEjgV9F3sBRq14eTERK9w&s"}
                        style={{ marginRight: "10px", borderRadius: "50%" }}
                        size={32}
                      />
                    </Col>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
                        <h4 style={{ margin: 0, fontSize: "13px" }}>{singleComment.fullName}</h4>
                        {/* Show edit icon only if user can edit */}
                        {canEdit() && !editing && (
                          <EditOutlined
                            style={{ cursor: "pointer", color: "#000000ff", marginLeft: "10px" }}
                            onClick={handleEdit}
                          />
                        )}
                      </div>
                      <QuillHtmlDisplay html={singleComment.comments || ""} style={{ marginTop: "8px" }} />
                      <Col align="end">
                        <h6 style={{ marginLeft: "8px", marginTop: "10px" }}>
                          {singleComment.commentDate?.join("/") || "N/A"}
                        </h6>
                      </Col>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* No comment message - show create button only if user can edit */}
            {!editing && !singleComment && !loading && (
              <div
                style={{
                  borderTop: "1px solid #e8e8e8",
                  padding: "20px",
                  background: "#fff",
                  width: "100%",
                  marginTop: "20px",
                  textAlign: "center",
                }}
              >
                <p>No {item} available for this region.</p>
                {canEdit() && (
                  <Button type="primary" onClick={handleEdit}>
                    Create {item}
                  </Button>
                )}
              </div>
            )}

            {/* Display message when user doesn't have edit permission but comment exists */}
            {!canEdit() && singleComment && !editing && (
              <div
                style={{
                  borderTop: "1px solid #e8e8e8",
                  padding: "8px",
                  background: "#f9f9f9",
                  width: "100%",
                  marginTop: "20px",
                }}
              >
                
                <div style={{ textAlign: "center", marginTop: "10px", color: "#999", fontSize: "12px" }}>
                  You don't have permission to edit this {item}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkFlowComponent;